import { useEffect, useState } from "react";
import { WarehouseOutwardModel } from "../model/warehouse_outward_model";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchPut } from "../../content_panel/services/serviceClient";
import LoadingWidget from "../../../../context_providers/modal/loader";
import { getOutwardDestinationLabel } from "../warehouse_helper";
import { InwardAttachmentUpload } from "../inward/InwardAttachmentUpload";

type Props = {
  open: boolean;
  onClose: () => void;
  outward: WarehouseOutwardModel;
  onSuccess: () => void;
};

export default function UploadReverseDcDocsModal({
  open,
  onClose,
  outward,
  onSuccess,
}: Props) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dcFiles, setDcFiles] = useState<File[]>([]);
  const [ewayFiles, setEwayFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!open) {
      setDcFiles([]);
      setEwayFiles([]);
    }
  }, [open]);

  // Both documents are mandatory — the backend rejects a partial upload, so the
  // submit stays disabled until each input has a file.
  const bothPresent = dcFiles.length > 0 && ewayFiles.length > 0;

  const getTotalValue = () =>
    outward?.items?.reduce(
      (sum: number, i: any) => sum + (i.itemPrice || 0) * (i.requestedQty || 0),
      0,
    ) || 0;

  const handleUpload = async () => {
    if (!bothPresent) {
      showToast({
        type: ToastType.warning,
        text: "Upload both the DC and the e-way bill",
      });
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("dc_attachment", dcFiles[0]);
      formData.append("eway_attachment", ewayFiles[0]);

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/upload-reverse-dc-docs/${outward.id}`,
        formData,
        { "Content-Type": "multipart/form-data" },
      );

      if (res.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "DC and e-way bill uploaded",
        });
        onSuccess?.();
        onClose();
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Upload failed",
        });
      }
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message || "Upload failed",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      id="warehouse-finance-upload-reverse-dc-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-lg bg-white shadow-lg rounded-lg flex flex-col wh-modal-xs">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Upload Reverse DC Documents</h2>
        </div>

        <div className="px-6 py-5 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              Upload both the DC and the e-way bill. The transfer moves to Ready
              To Dispatch only when both are attached.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
            <p className="flex justify-between">
              <strong className="text-gray-700">Transfer:</strong>
              <span className="text-gray-900 font-semibold">{outward.id}</span>
            </p>
            <p className="flex justify-between">
              <strong className="text-gray-700">Pickup from:</strong>
              <span className="text-gray-900 font-semibold">
                {getOutwardDestinationLabel(outward)}
              </span>
            </p>
            <p className="flex justify-between">
              <strong className="text-gray-700">Total Value:</strong>
              <span className="text-blue-600 font-bold text-lg">
                ₹{getTotalValue().toLocaleString()}
              </span>
            </p>
          </div>

          <div
            id="warehouse-finance-reverse-dc-attachment-wrapper"
            className="space-y-2"
          >
            <label className="block text-sm font-medium text-gray-700">
              DC Attachment *
            </label>
            <InwardAttachmentUpload
              accept="application/pdf,image/jpeg,image/png"
              maxSizeMB={5}
              files={dcFiles}
              multiple={false}
              onChange={setDcFiles}
            />
          </div>

          <div
            id="warehouse-finance-reverse-eway-attachment-wrapper"
            className="space-y-2"
          >
            <label className="block text-sm font-medium text-gray-700">
              E-way Bill Attachment *
            </label>
            <InwardAttachmentUpload
              accept="application/pdf,image/jpeg,image/png"
              maxSizeMB={5}
              files={ewayFiles}
              multiple={false}
              onChange={setEwayFiles}
            />
          </div>

          {!bothPresent && (dcFiles.length > 0 || ewayFiles.length > 0) && (
            <p className="text-xs text-amber-600">
              Both documents are required —{" "}
              {dcFiles.length === 0 ? "DC" : "e-way bill"} still missing.
            </p>
          )}
        </div>

        <div className="border-t px-6 py-4 flex gap-3">
          <button
            id="warehouse-finance-upload-reverse-dc-cancel-btn"
            onClick={onClose}
            className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>

          <button
            id="warehouse-finance-upload-reverse-dc-submit-btn"
            onClick={handleUpload}
            disabled={loading || !bothPresent}
            className={`flex-1 h-12 rounded-lg font-medium shadow-lg ${
              loading || !bothPresent
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {loading ? "Uploading..." : "Upload & Mark Ready"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
